import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { API_ROUTES, type ListResponse, type Tournament, type TournamentStatus } from '@brochure/shared'
import { apiFetch } from '@/lib/api'
import { LocalizedLink } from '@/components/LocalizedLink'
import { Eyebrow } from '@/components/ui/Eyebrow'
import { ArrowRight, Trophy } from 'lucide-react'
import { formatTournamentDate } from '@/lib/stoccopong-format'
import { cn } from '@/lib/utils'

// Striscia del biglietto per stato: sole = iscrizioni aperte, corallo = in corso, inchiostro tenue = chiuso/concluso.
const STRIPS: Record<TournamentStatus, string> = {
  registration: 'bg-sun/25 text-sun-ink',
  active: 'bg-coral/15 text-coral-ink',
  closed: 'bg-ink/5 text-ink-soft',
  completed: 'bg-ink/5 text-ink-soft',
}

// Ordine di visualizzazione: iscrizioni aperte prima, poi in corso, poi il resto (chiuso/concluso).
const STATUS_RANK: Record<TournamentStatus, number> = { registration: 0, active: 1, closed: 2, completed: 3 }

function sortTournaments(tournaments: readonly Tournament[]): Tournament[] {
  return [...tournaments].sort((a, b) => STATUS_RANK[a.status] - STATUS_RANK[b.status])
}

/** Lista pubblica dei tornei stocco pong, ordinata per stato: si iscrive prima chi guarda per primo. */
export function TournamentsPage() {
  const { t, i18n } = useTranslation()
  const [tournaments, setTournaments] = useState<Tournament[] | null>(null)
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    const controller = new AbortController()
    apiFetch<ListResponse<Tournament>>(`${API_ROUTES.tournaments}?sort=date&order=desc&limit=100`, { signal: controller.signal })
      .then((response) => setTournaments(sortTournaments(response.items)))
      .catch(() => { if (!controller.signal.aborted) setHasError(true) })
    return () => controller.abort()
  }, [])

  return (
    <main id="main" className="mx-auto max-w-7xl px-4 pt-24 pb-section sm:px-6 md:pt-28 lg:px-8">
      <Eyebrow>{t('stoccoPong.eyebrow')}</Eyebrow>
      <h1 className="hero-title mt-2 text-hero text-ink lg:text-[5.5rem]/[0.95]">{t('stoccoPong.title')}</h1>

      {hasError && <p role="alert" className="mt-10 max-w-prose text-body text-ink-soft">{t('stoccoPong.error.list')}</p>}
      {tournaments?.length === 0 && <p className="mt-10 max-w-prose text-body text-ink-soft">{t('stoccoPong.empty')}</p>}

      <ul className="mt-8 grid gap-5 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
        {tournaments?.map((tournament) => (
          <li key={tournament.id}>
            {/* Carta d'imbarco come i biglietti della home: striscia con lo stato, perforazione, destinazione. */}
            <LocalizedLink
              to="stoccoPongTournament"
              params={{ id: tournament.id }}
              className="group flex flex-col overflow-hidden rounded-xl border-2 border-ink bg-paper text-ink shadow-[4px_4px_0_var(--color-ink)] transition-[transform,box-shadow] duration-300 ease-out-expo hover:-translate-y-1 hover:shadow-[6px_9px_0_var(--color-ink)] active:translate-y-0.5 active:shadow-[1px_1px_0_var(--color-ink)] active:duration-75"
            >
              <span className={cn('flex items-center justify-between gap-2 px-5 pt-3.5 pb-3 font-body text-[0.65rem] font-semibold uppercase tracking-[0.2em]', STRIPS[tournament.status])}>
                {t(`stoccoPong.status.${tournament.status}`)}
                <Trophy aria-hidden="true" className="size-4 transition-transform duration-500 ease-out-expo group-hover:rotate-12" />
              </span>
              <span aria-hidden="true" className="ticket-perforation" />
              <span className="px-5 pt-3 pb-4">
                <span className="block font-display text-3xl font-medium leading-tight tracking-tight">{tournament.name}</span>
                <span className="mt-2 flex items-center justify-between gap-3 font-body text-sm text-ink-soft">
                  <span>
                    {formatTournamentDate(tournament.date, i18n.language)}
                    <span className="mx-2 text-line">|</span>
                    {tournament.teamCount > 0
                      ? t('stoccoPong.counts.teams', { count: tournament.teamCount })
                      : t('stoccoPong.counts.participants', { count: tournament.participantCount })}
                  </span>
                  <ArrowRight aria-hidden="true" className="size-4 shrink-0 transition-transform duration-300 ease-out-expo group-hover:translate-x-1" />
                </span>
              </span>
            </LocalizedLink>
          </li>
        ))}
      </ul>
    </main>
  )
}
